import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { config } from '../core/config';
import AuthService from './auth';
import { axiosRetry } from '../utils/networkRetry';
import {
  LoginCredentials,
  LoginResult,
  VehicleModel,
  Operator,
  FileSigningOptions,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  ConfigVersion,
  ApiError
} from '../types/api.types';

class ApiService {
  private axiosInstance: AxiosInstance;
  private loginAxiosInstance: AxiosInstance;

  constructor() {
    // Check if running locally with Vite proxy
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Main API instance - will use tenant-based URLs after login
    this.axiosInstance = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Login API instance - uses proxy in local dev, or direct URL in production
    this.loginAxiosInstance = axios.create({
      baseURL: isLocalDev ? '/api' : `https://${config.apiHost}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    this.setupInterceptors();
    
    // Add retry logic to both instances
    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: 1000,
      exponentialBackoff: true,
      onRetry: (retryCount, error) => {
        console.log(`Retrying request (attempt ${retryCount}):`, error.message);
      }
    });
    
    axiosRetry(this.loginAxiosInstance, {
      retries: 2,
      retryDelay: 500
    });
  }

  private async updateBaseUrl() {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const userData = await AuthService.getUserData();
    
    if (isLocalDev) {
      // Use proxy for local development
      this.axiosInstance.defaults.baseURL = '/api';
    } else if (userData?.tenant) {
      // Check if the apiHost already includes the tenant
      // (e.g., "uptime.dev.shzoom.com" already has "uptime")
      if (config.apiHost.startsWith(userData.tenant + '.')) {
        // Tenant is already in the host, use as-is
        this.axiosInstance.defaults.baseURL = `https://${config.apiHost}/api`;
      } else {
        // Tenant not in host, prepend it
        this.axiosInstance.defaults.baseURL = `https://${userData.tenant}.${config.apiHost}/api`;
      }
    } else {
      // Fallback to default if no tenant
      this.axiosInstance.defaults.baseURL = config.apiBaseUrl;
    }
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and update base URL
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Update base URL before each request
        await this.updateBaseUrl();
        
        const token = await AuthService.getToken();
        
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          // Don't auto-redirect for vehicle lookups - let the component handle it
          if (!error.config?.url?.includes('/vehicle/')) {
            await AuthService.clearAuth();
            window.location.href = '/login';
          }
        }
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'An unexpected error occurred';
        
        return Promise.reject({
          message: errorMessage,
          statusCode: error.response?.status,
          error: error.response?.data
        });
      }
    );
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<LoginResult> {
    // Use the login-specific instance
    const response = await this.loginAxiosInstance.post<any>('/auth/login', {
      username: credentials.email,
      password: credentials.password,
      tenant: config.tenant
    });
    
    // Handle double-serialized JSON from .NET API
    let loginData = response.data;
    if (typeof loginData === 'string') {
      try {
        loginData = JSON.parse(loginData);
      } catch (e) {
        console.error('Failed to parse login response as JSON:', e);
      }
    }
    
    await AuthService.setAuth(loginData);
    
    // Update the base URL now that we have the tenant
    await this.updateBaseUrl();
    
    return loginData;
  }

  async logout(): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await AuthService.clearAuth();
    }
  }

  async checkVersion(): Promise<ConfigVersion> {
    const response = await this.axiosInstance.get<ConfigVersion>('/config/version');
    return response.data;
  }

  // Vehicle endpoints
  async getVehicle(vinOrCode: string): Promise<VehicleModel> {
    const response = await this.axiosInstance.get<VehicleModel>(`/vehicle/${vinOrCode}`);
    
    // Handle double-serialized JSON if needed
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse vehicle response as JSON:', e);
      }
    }
    
    return data;
  }

  // Operator endpoints
  async getOperators(): Promise<Operator[]> {
    const response = await this.axiosInstance.get<Operator[]>('/operator/');
    return response.data;
  }

  // File upload endpoints
  async getFileSigningOptions(): Promise<FileSigningOptions> {
    const response = await this.axiosInstance.get<FileSigningOptions>('/fileupload/getsigningoptions');
    return response.data;
  }

  async uploadPhotoToAzure(url: string, blob: Blob): Promise<void> {
    await axios.put(url, blob, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/octet-stream'
      }
    });
  }

  // Incident endpoints
  async createIncident(incident: CreateIncidentRequest): Promise<{ id: number }> {
    const response = await this.axiosInstance.post<{ id: number }>('/Incident/create', incident);
    return response.data;
  }

  async updateIncident(incident: UpdateIncidentRequest): Promise<{ id: number }> {
    const response = await this.axiosInstance.put<{ id: number }>('/Incident/update', incident);
    return response.data;
  }

  // Generic request method for flexibility
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.request<T>(config);
    return response.data;
  }
}

export const Api = new ApiService();
export default Api;